/**
 * The map library.
 *
 * Every floor of a run is a real competitive map — footprint traced from its
 * published radar, elevation from the radar's own shading, compiled and gated
 * like anything else. A generated floor is provably fair and still reads as
 * generated; these read as places, because they were designed as places.
 *
 * The plans are bundled as text and compiled in the browser. That is a few
 * hundred kilobytes for the whole library rather than tens of megabytes of
 * baked geometry, and it means a map is one file a person can edit.
 */

import type { GeneratedLevel, MapLook, Theme } from 'levelspec/generate';
import { loadAuthored, LOOKS } from 'levelspec/generate';
import { profileFor } from './art.ts';

/**
 * Each map's own look, derived from its screenshot and hand-corrected for the
 * ones people recognise. This replaces the six generic biomes: a hundred maps
 * drawing from six palettes means every sixth map is the same colour, which is
 * most of what made the library feel machine-made.
 */

const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/** Turn a look into the Theme shape the renderer already speaks. */
function themeFor(id: string, name: string, fallback: Theme): Theme {
  // By id first, then by the map's display name, then without an upper/lower
  // suffix — an authored map's filename need not match the archive's folder,
  // and the two halves of a two-storey map share one look.
  const l: MapLook | undefined =
    LOOKS[id] ??
    LOOKS[slug(name)] ??
    LOOKS[id.replace(/_(upper|lower)$/, '')] ??
    LOOKS[slug(name).replace(/_(upper|lower)$/, '')];
  if (!l) return fallback;
  return {
    ...fallback,
    blurb: l.look ?? fallback.blurb,
    palette: {
      roles: l.roles,
      fog: l.fog, fogNear: l.fogNear, fogFar: l.fogFar,
      sky: l.sky,
      sun: l.sun, sunIntensity: l.sunIntensity,
      hemiSky: l.hemiSky, hemiGround: l.hemiGround, hemiIntensity: l.hemiIntensity,
      grid: { line: l.grid.line, alpha: l.grid.alpha },
      emissive: fallback.palette.emissive,
    },
    surface: l.surface,
    gridScale: l.grid.scale,
    skyline: l.skyline,
    facade: l.facade,
    roofs: l.roofs,
    blocks: l.blocks,
    accent: l.accent,
    daylight: l.daylight,
  };
}

const traced = import.meta.glob('../../../levelspec/maps/cs/*.plan', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/**
 * Hand-authored maps, which win over a trace of the same place.
 *
 * A trace gets a footprint and guesses everything else. An authored map knows
 * that Catwalk joins Mid to A Short, that the Pit is sunk below Long, and that
 * the site is raised — so it has real callouts, real elevation, ramps where
 * ramps belong and cover where cover belongs. As these are written they simply
 * replace the traced version.
 */
const handMade = import.meta.glob('../../../levelspec/maps/hand/*.plan', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const plans: Record<string, string> = { ...traced };
for (const [path, text] of Object.entries(handMade)) {
  const id = path.split('/').pop()!.replace('.plan', '');
  // Drop any traced map of the same place before adding the authored one.
  const m = /^name:\s*(.+)$/m.exec(text);
  const name = m?.[1].trim().toLowerCase();
  for (const key of Object.keys(plans)) {
    const other = /^name:\s*(.+)$/m.exec(plans[key]);
    if (other && other[1].trim().toLowerCase() === name) delete plans[key];
  }
  plans[`hand/${id}`] = text;
}

export interface MapEntry {
  id: string;
  /** Display name, taken from the plan's own header. */
  name: string;
  text: string;
  /** Roughly how much floor there is, for ordering easy maps first. */
  size: number;
}

function titleOf(text: string, fallback: string): string {
  const m = /^name:\s*(.+)$/m.exec(text);
  return m ? m[1].trim() : fallback;
}

/**
 * Floor cells in the drawn plan — a cheap proxy for how big the map plays.
 *
 * Every plan block, not just the first: a two-storey map plays as the sum of
 * its storeys, and counting only the ground floor would file it with the small
 * ones and open a run on it.
 */
function sizeOf(text: string): number {
  const lines = text.split('\n');
  let n = 0;
  let inPlan = false;
  for (const line of lines) {
    if (line.trim() === 'plan') { inPlan = true; continue; }
    if (line.trim() === 'end') { inPlan = false; continue; }
    if (!inPlan) continue;
    for (const ch of line) if (ch !== '.' && ch !== '#' && ch !== ' ') n++;
  }
  return n;
}

/**
 * Floor cells below which a map is not a map.
 *
 * Some radars are published as an upper and a lower sheet, and the tracer
 * reads each on its own — which for a mostly-ghosted upper sheet yields a
 * two-cell corridor with sixty-nine cells of floor in it. That is not a small
 * map, it is half of one, and a floor with no room to move is a floor a run
 * can stall on: the bot found exactly that, and spent a hundred and fifty
 * seconds on `pitstop_upper` unable to reach the last enemy.
 */
const MIN_CELLS = 300;

/**
 * The library, smallest first.
 *
 * Where two plans are the same place — the two halves of a split radar — only
 * the larger survives, unless a hand-authored map has already claimed the name
 * and joined them properly, which is what `nuke` does.
 */
const entries = Object.entries(plans)
  .map(([path, text]) => {
    const id = path.split('/').pop()!.replace('.plan', '');
    return { id, name: titleOf(text, id), text, size: sizeOf(text) };
  })
  .filter((m) => m.size >= MIN_CELLS);

const biggest = new Map<string, MapEntry>();
for (const m of entries) {
  const k = m.name.toLowerCase();
  const had = biggest.get(k);
  if (!had || m.size > had.size) biggest.set(k, m);
}

export const MAPS: MapEntry[] = [...biggest.values()].sort((a, b) => a.size - b.size);

/**
 * The order a run visits maps in.
 *
 * Small maps first: a first floor you can cross in twenty seconds teaches the
 * controls, and by floor ten the maps are the big ones. Within a size band the
 * order is shuffled from the run's seed, so two runs never march through the
 * same list even though both ramp the same way.
 */
export function runOrder(seed: number, length = 30): MapEntry[] {
  let s = seed >>> 0 || 1;
  const rnd = (): number => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
  const bands = 6;
  const per = Math.ceil(MAPS.length / bands);
  const out: MapEntry[] = [];
  for (let b = 0; b < bands; b++) {
    const slice = MAPS.slice(b * per, (b + 1) * per);
    for (let i = slice.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [slice[i], slice[j]] = [slice[j], slice[i]];
    }
    out.push(...slice);
  }

  /*
   * No two floors in a row that look like the same place.
   *
   * Every map is now art-directed — a light, an accent and a kind of building
   * it is pretending to be — and a shuffle that is blind to that will happily
   * deal three overcast civic halls in a row. Three overcast civic halls in a
   * row is one map played three times, whatever the floorplans say, because
   * what a player remembers about a floor is its light and its colour long
   * after they have forgotten its shape.
   *
   * So a floor whose look matches the one before it swaps with the next map in
   * its own band that does not. Within a band, not across: the size ramp is
   * what makes floor one crossable in twenty seconds, and it outranks this.
   */
  const look = (m: MapEntry): string => {
    const p = profileFor(m.id);
    return `${p.lighting}|${p.language}`;
  };
  for (let b = 0; b < bands; b++) {
    const lo = b * per;
    const hi = Math.min(out.length, lo + per);
    for (let i = lo + 1; i < hi; i++) {
      if (look(out[i]) !== look(out[i - 1])) continue;
      for (let j = i + 1; j < hi; j++) {
        if (look(out[j]) === look(out[i - 1])) continue;
        [out[i], out[j]] = [out[j], out[i]];
        break;
      }
    }
  }
  // Runs longer than the library wrap round rather than ending.
  while (out.length < length) out.push(...out.slice(0, length - out.length));
  return out.slice(0, length);
}

export function loadMap(entry: MapEntry, depth: number, seed: string): GeneratedLevel {
  const gen = loadAuthored({ id: entry.id, text: entry.text }, { depth, seed });
  gen.theme = themeFor(entry.id, entry.name, gen.theme);
  return gen;
}
